(() => {
  const READY_CLASS = 'hd-predesigned-product-page';
  const CATEGORY_TITLE = 'ClinicFit™ Dental Organizer';
  const RELATED_TITLE = 'More ClinicFit layouts';
  const manualSizeCache = new WeakMap();

  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  };

  const createProductName = (value) =>
    (value || '')
      .replace(/ClinicFit™/gi, '')
      .replace(/Dental Drawer Organizer/gi, '')
      .replace(/Dental Organizer/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

  const normalizeDimensionSource = (value) =>
    (value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[“”″]/g, '"')
      .replace(/\s+/g, ' ')
      .trim();

  const dimensionSignature = (value) => {
    const match = normalizeDimensionSource(value).match(
      /(\d+(?:\.\d+)?)\s*"?\s*(?:×|x)\s*(\d+(?:\.\d+)?)\s*"?\s*(?:×|x)\s*(\d+(?:\.\d+)?)\s*"?/i
    );

    return match ? match.slice(1, 4).join('|') : '';
  };

  const getManualSizeData = (root) => {
    if (!root) return { metricText: '', imperialSignature: '' };

    const cached = manualSizeCache.get(root);
    if (cached) return cached;

    const sourceElement =
      root.querySelector('[data-hdpp-manual-spec-source]') ||
      document.querySelector('[data-hdpp-manual-spec-source]');

    const textBlocks = sourceElement
      ? Array.from(sourceElement.querySelectorAll('li, p'))
          .map((element) => element.textContent || '')
          .filter(Boolean)
          .join(' | ')
      : '';
    const sourceText = normalizeDimensionSource(textBlocks || sourceElement?.textContent || '');
    const metricMatch = sourceText.match(
      /(\d+(?:\.\d+)?)\s*(?:×|x)\s*(\d+(?:\.\d+)?)\s*(?:×|x)\s*(\d+(?:\.\d+)?)\s*mm\b/i
    );

    const result = {
      metricText: metricMatch
        ? `${metricMatch[1]} × ${metricMatch[2]} × ${metricMatch[3]} mm`
        : '',
      imperialSignature: metricMatch
        ? dimensionSignature(sourceText.slice(0, metricMatch.index))
        : ''
    };

    manualSizeCache.set(root, result);
    return result;
  };

  const splitSeoTitle = (heading) => {
    if (!heading || heading.dataset.hdppTitleSplit === 'true') return;

    const originalTitle = heading.textContent.replace(/\s+/g, ' ').trim();
    const productName = createProductName(originalTitle);
    if (!productName) return;

    const category = createElement('span', 'hdpp-title-category', CATEGORY_TITLE);
    const name = createElement('span', 'hdpp-title-name', productName);

    heading.replaceChildren(category, document.createTextNode(' '), name);
    heading.dataset.hdppTitleSplit = 'true';
    heading.setAttribute('aria-label', originalTitle);
  };

  const formatSpecificationSize = (root) => {
    const { metricText } = getManualSizeData(root);
    if (!metricText) return;

    document.querySelectorAll('.hdpp-spec-grid > div').forEach((item) => {
      const label = normalize(item.querySelector('dt')?.textContent);
      const value = item.querySelector('dd');

      if (label !== 'size' || !value || value.dataset.hdppMetricAdded === 'true') return;

      const imperialText = value.textContent.replace(/\s+/g, ' ').trim();
      if (!imperialText || /\bmm\b/i.test(imperialText)) return;

      const imperial = createElement('span', 'hdpp-size-imperial', imperialText);
      const metric = createElement('span', 'hdpp-size-metric', metricText);

      value.replaceChildren(imperial, metric);
      value.dataset.hdppMetricAdded = 'true';
    });
  };

  const formatSizeOptions = (root) => {
    const { metricText, imperialSignature } = getManualSizeData(root);
    if (!metricText) return;

    document.querySelectorAll('.product-form__input').forEach((group) => {
      const groupLabel = normalize(group.querySelector('.form__label, legend')?.textContent);
      if (!groupLabel.startsWith('size')) return;

      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const label = input.nextElementSibling;
        if (!label || label.tagName !== 'LABEL' || label.dataset.hdppMetricAdded === 'true') return;

        const optionSignature = dimensionSignature(input.value);
        if (!optionSignature) return;
        if (imperialSignature && optionSignature !== imperialSignature) return;

        label.appendChild(createElement('span', 'hdpp-size-option-metric', metricText));
        label.classList.add('hdpp-size-option-with-mm');
        label.dataset.hdppMetricAdded = 'true';
      });
    });
  };

  const getLidOptionValues = () => {
    for (const group of document.querySelectorAll('.product-form__input')) {
      const groupLabel = normalize(group.querySelector('.form__label, legend')?.textContent);
      if (!groupLabel.startsWith('lid')) continue;

      const values = Array.from(group.querySelectorAll('input[type="radio"]'))
        .map((input) => (input.value || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean);

      if (values.length > 0) return [...new Set(values)];

      const select = group.querySelector('select');
      if (select) {
        return [...new Set(Array.from(select.options).map((option) => option.textContent.trim()).filter(Boolean))];
      }
    }

    return [];
  };

  const formatLidConfiguration = () => {
    document.querySelectorAll('.hdpp-spec-grid > div').forEach((item) => {
      const label = normalize(item.querySelector('dt')?.textContent);
      const value = item.querySelector('dd');

      if (label !== 'lid configuration' || !value || value.dataset.hdppLidFormatted === 'true') return;

      let options = getLidOptionValues();
      if (options.length < 2) {
        options = value.textContent
          .split(',')
          .map((option) => option.replace(/\s+/g, ' ').trim())
          .filter(Boolean);
      }

      if (options.length < 2) return;

      const uniqueOptions = [...new Set(options)];
      value.classList.add('hdpp-lid-options');
      value.replaceChildren(...uniqueOptions.map((option) => createElement('span', '', option)));
      value.dataset.hdppLidFormatted = 'true';
    });
  };

  const formatProductDetails = (root) => {
    formatSpecificationSize(root);
    formatSizeOptions(root);
    formatLidConfiguration();
  };

  const removeLegacyProductContent = () => {
    const info =
      document.querySelector('product-info[id^="MainProduct-"] .product__info-container') ||
      document.querySelector('.product__info-container');

    if (info) {
      info.querySelectorAll('.product__description, .share-button, share-button').forEach((element) => element.remove());
      info.querySelectorAll('.product__text.caption-with-letter-spacing').forEach((element) => element.remove());
      info.querySelectorAll('.hdpp-support-link, .hdpp-purchase-trust').forEach((element) => element.remove());

      info.querySelectorAll('.product__accordion').forEach((accordion) => {
        const label = normalize(accordion.querySelector('summary, .accordion__title')?.textContent);
        if (label.includes('how to measure') || label.includes('specification')) accordion.remove();
      });
    }

    document.querySelectorAll('#MainContent .shopify-section').forEach((section) => {
      const id = normalize(section.id).replace(/\s+/g, '');
      if (id.includes('shipping_returns') || id.includes('shipping-returns')) section.remove();
    });
  };

  const enhancePurchasePanel = (root) => {
    const info =
      document.querySelector('product-info[id^="MainProduct-"] .product__info-container') ||
      document.querySelector('.product__info-container');

    if (!info) return;

    splitSeoTitle(info.querySelector('.product__title h1'));

    const titleBlock = info.querySelector('.product__title');
    if (titleBlock && !info.querySelector('.hdpp-hero-lead')) {
      const lead = createElement('p', 'hdpp-hero-lead', root.dataset.hdppSummary || '');
      titleBlock.insertAdjacentElement('afterend', lead);
    }

    const purchaseForm = Array.from(info.querySelectorAll('product-form')).find((form) =>
      form.querySelector('.product-form__submit')
    );
    const submitButton = purchaseForm?.querySelector('.product-form__submit');
    const actionRow = purchaseForm?.querySelector('.product-form__buttons');

    if (!purchaseForm || !submitButton || !actionRow) return;

    actionRow.classList.add('hdpp-purchase-actions');

    let sizingBubble = actionRow.querySelector('.hdpp-sizing-bubble');
    if (!sizingBubble) {
      sizingBubble = createElement('a', 'hdpp-sizing-bubble', 'Get sizing help');
      sizingBubble.href = '#shopify-inbox-support';
      sizingBubble.setAttribute('data-hd-inbox-trigger', '');
      sizingBubble.setAttribute('aria-label', 'Open sizing help chat');
      submitButton.insertAdjacentElement('afterend', sizingBubble);
    }
  };

  const updateRelatedProducts = () => {
    document.querySelectorAll('.related-products__heading').forEach((heading) => {
      if (heading.textContent.trim() !== RELATED_TITLE) heading.textContent = RELATED_TITLE;
    });
  };

  const initialize = () => {
    const root = document.querySelector('[data-hdpp-content]');
    if (!root) return;

    document.documentElement.classList.add(READY_CLASS);
    document.body?.classList.add(READY_CLASS);

    removeLegacyProductContent();
    enhancePurchasePanel(root);
    formatProductDetails(root);
    updateRelatedProducts();

    const mainContent = document.querySelector('#MainContent');
    if (!mainContent) return;

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;

      window.requestAnimationFrame(() => {
        scheduled = false;
        removeLegacyProductContent();
        enhancePurchasePanel(root);
        formatProductDetails(root);
        updateRelatedProducts();
      });
    });

    observer.observe(mainContent, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
